import { useEffect, useRef, useCallback, useState } from "react"
import { useAudioSettings } from "@/lib/audio-settings"

// Level music mapping - will be expanded for different levels later
const LEVEL_MUSIC: Record<number, string> = {
  1: "/sounds/levels/pelf_level1.ogg",
  2: "/sounds/levels/pelf_level1.ogg",
  3: "/sounds/levels/pelf_level1.ogg",
  4: "/sounds/levels/pelf_level1.ogg",
  5: "/sounds/levels/pelf_level1.ogg",
  6: "/sounds/levels/pelf_level1.ogg",
  7: "/sounds/levels/pelf_level1.ogg",
  8: "/sounds/levels/pelf_level1.ogg",
  9: "/sounds/levels/pelf_level1.ogg",
  10: "/sounds/levels/pelf_level1.ogg",
}

const DEFAULT_VOLUME = 0.3

interface UseBackgroundMusicOptions {
  levelNumber: number
  isPlaying: boolean // true when game is in "playing" state
  isPaused: boolean // true when game is paused
  isAbducting?: boolean // true when abduction animation is playing
}

export function useBackgroundMusic({
  levelNumber,
  isPlaying,
  isPaused,
  isAbducting = false,
}: UseBackgroundMusicOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [volume, setVolume] = useState(DEFAULT_VOLUME)
  // Global music volume setting scales the hook's base volume
  const { music: musicSetting } = useAudioSettings()
  const hasUserInteracted = useRef(false)

  // Track user interaction to enable audio playback
  useEffect(() => {
    const handleInteraction = () => {
      hasUserInteracted.current = true
      // Try to play if conditions are met
      if (isPlaying && !isPaused && audioRef.current?.paused) {
        audioRef.current.play().catch(() => {})
      }
    }

    window.addEventListener("click", handleInteraction, { once: false })
    window.addEventListener("touchstart", handleInteraction, { once: false })
    window.addEventListener("keydown", handleInteraction, { once: false })

    return () => {
      window.removeEventListener("click", handleInteraction)
      window.removeEventListener("touchstart", handleInteraction)
      window.removeEventListener("keydown", handleInteraction)
    }
  }, [isPlaying, isPaused])

  // Initialize audio element for the level
  useEffect(() => {
    const musicUrl = LEVEL_MUSIC[levelNumber] || LEVEL_MUSIC[1]

    // Create new audio element
    const audio = new Audio(musicUrl)
    audio.loop = true
    audio.volume = isMuted ? 0 : volume * musicSetting
    audioRef.current = audio

    return () => {
      // Cleanup: stop and remove audio
      audio.pause()
      audio.src = ""
      audioRef.current = null
    }
  }, [levelNumber]) // Only recreate when level changes

  // Update volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume * musicSetting
    }
  }, [volume, isMuted, musicSetting])

  // Handle play/pause based on game state
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    // Stop music when abducting or not playing
    if (isPlaying && !isPaused && !isAbducting) {
      // Only try to play if user has interacted
      if (hasUserInteracted.current) {
        audio.play().catch(() => {
          // Autoplay was prevented, will play on next user interaction
        })
      }
    } else {
      audio.pause()
    }
  }, [isPlaying, isPaused, isAbducting])

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev)
  }, [])

  const setMusicVolume = useCallback((newVolume: number) => {
    setVolume(Math.max(0, Math.min(1, newVolume)))
  }, [])

  return {
    isMuted,
    volume,
    toggleMute,
    setVolume: setMusicVolume,
  }
}
